import React from "react";
import {
  PullPhoneReportParams,
  CombinedReportWithNote,
  PullVehicleReportParams,
  PullNeighborhoodReportParams,
} from "../../../features/reports/reportTypes";
import {
  ContainerLineItem,
  Icon,
  IconList,
  Layout,
  LoadingPlaceholder,
  ReportLink,
  Text,
} from "@kidslivesafe/sc-react";
import {
  PullPeopleReportParams,
  usePeopleReportDispatch,
} from "../../../hooks/peopleReportHook";

import { usePhoneReportDispatch } from "../../../hooks/phoneReportHook";

import {
  PullPropertyReportParams,
  usePropertySearchDispatch,
} from "../../../hooks/propertyReportHook";

import { useCurrentBreakpoint } from "@kidslivesafe/sc-foundation";
import { useSocialSearchDispatch } from "../../../hooks/socialSearchHook";
import { useNeighborhoodReportDispatch } from "../../../hooks/neighborhoodReportHook";
import { useAppSelector } from "../../../hooks/hooks";
import { selectBrandInfo } from "../../../features/brand/brandSlice";
import { parseDate } from "../../../utils";
import { useVehicleReportDispatch } from "../../../hooks/vehicleReportHook";

interface NoteLineItemProps {
  note: CombinedReportWithNote | undefined;
  loading?: boolean;
  loadingKey?: string | number;
  noPadding?: boolean;
}

const NoteLineItem: React.FC<NoteLineItemProps> = ({
  note,
  loading = false,
  loadingKey,
  noPadding = false,
}) => {
  const pullPeopleReport = usePeopleReportDispatch();
  const searchSocial = useSocialSearchDispatch();
  const pullPhoneReport = usePhoneReportDispatch();
  const pullPropertyReport = usePropertySearchDispatch();
  const pullNeighborhoodReport = useNeighborhoodReportDispatch();
  const pullVehicleReport = useVehicleReportDispatch();
  const brandInfo = useAppSelector(selectBrandInfo);

  const currentBreakpoint = useCurrentBreakpoint();

  const detailsSpacing = currentBreakpoint === "desktop" ? "md" : "xxs";
  // helper functions.

  // render history based on report type
  const renderListItems = (note: CombinedReportWithNote | undefined) => {
    if (!note) return null;
    switch (note.reportType) {
      case "people":
        return (
          <ContainerLineItem
            key={note.id}
            lineItemHeader={
              <Layout
                type="flex"
                flexProps={{ gap: "xxs", alignItems: "center" }}
              >
                <ReportLink<PullPeopleReportParams>
                  noWrap
                  handleClick={inputValue =>
                    pullPeopleReport(inputValue, "note")
                  }
                  inputValue={note.reportToken}
                  text={note.displayName}
                />
              </Layout>
            }
            lineItemSubHeader={note.reportDetails}
            lineItemSubHeaderLine2={
              <Text size="xs">
                <Text tag="b" size="xs">
                  {note.reportNotesCount} Note{note.reportNotesCount > 1 && "s"}
                </Text>{" "}
                - Modified on {parseDate(note.lastActivityDate, "MM/DD/YYYY")}
              </Text>
            }
            lineItemHeaderNoWrap
            lineItemSubheaderNoWrap
            lineItemDetails={
              <Layout
                type="flex"
                flexProps={{ gap: detailsSpacing, alignItems: "center" }}
              >
                <Icon size={32} name={IconList.IconPeople} />
              </Layout>
            }
            noPadding={noPadding}
          />
        );
      case "property":
        return (
          <ContainerLineItem
            key={note.id}
            lineItemHeader={
              <ReportLink<PullPropertyReportParams>
                noWrap
                text={note.displayName}
                inputValue={{
                  addressLine1: note.addressLine1,
                  addressLine2: note.addressLine2,
                  state: note.state,
                  city: note.city,
                  zip: note.zip,
                  nearbySchoolRadius: 5,
                  searchMethod: "LinkClick",
                }}
                handleClick={inputValue =>
                  pullPropertyReport(inputValue, "note")
                }
              />
            }
            lineItemSubHeader={note.reportDetails}
            lineItemSubHeaderLine2={
              <Text size="xs">
                <Text tag="b" size="xs">
                  {note.reportNotesCount} Note{note.reportNotesCount > 1 && "s"}
                </Text>{" "}
                - Modified on {parseDate(note.lastActivityDate, "MM/DD/YYYY")}
              </Text>
            }
            lineItemHeaderNoWrap
            lineItemSubheaderNoWrap
            lineItemDetails={
              <Layout
                type="flex"
                flexProps={{ gap: detailsSpacing, alignItems: "center" }}
              >
                <Text
                  tag="div"
                  color={["pf"].includes(brandInfo.acronym) ? "base" : "accent"}
                >
                  <Icon size={32} name={IconList.IconProperty} />
                </Text>
              </Layout>
            }
            noPadding={noPadding}
          />
        );
      case "social":
        const socialText = note.displayName;

        return (
          <ContainerLineItem
            key={note.id}
            lineItemHeader={
              <ReportLink
                noWrap
                inputValue={socialText}
                text={note.displayName}
                handleClick={inputValue => searchSocial(inputValue, "note")}
              />
            }
            lineItemSubHeader={note.reportDetails}
            lineItemSubHeaderLine2={
              <Text size="xs">
                <Text tag="b" size="xs">
                  {note.reportNotesCount} Note{note.reportNotesCount > 1 && "s"}
                </Text>{" "}
                - Modified on {parseDate(note.lastActivityDate, "MM/DD/YYYY")}
              </Text>
            }
            lineItemHeaderNoWrap
            lineItemSubheaderNoWrap
            lineItemDetails={
              <Layout
                type="flex"
                flexProps={{ gap: detailsSpacing, alignItems: "center" }}
              >
                <Icon size={32} name={IconList.IconSocial} />
              </Layout>
            }
            noPadding={noPadding}
          />
        );
      case "phone":
        return (
          <ContainerLineItem
            key={note.id}
            lineItemHeader={
              <ReportLink<PullPhoneReportParams>
                noWrap
                inputValue={note.phone}
                text={note.displayName}
                handleClick={inputValue => pullPhoneReport(inputValue, "note")}
              />
            }
            lineItemSubHeader={note.reportDetails}
            lineItemSubHeaderLine2={
              <Text size="xs">
                <Text tag="b" size="xs">
                  {note.reportNotesCount} Note{note.reportNotesCount > 1 && "s"}
                </Text>{" "}
                - Modified on {parseDate(note.lastActivityDate, "MM/DD/YYYY")}
              </Text>
            }
            lineItemHeaderNoWrap
            lineItemSubheaderNoWrap
            lineItemDetails={
              <Layout
                type="flex"
                flexProps={{ gap: detailsSpacing, alignItems: "center" }}
              >
                <Icon size={32} name={IconList.IconPhone} />
              </Layout>
            }
            noPadding={noPadding}
          />
        );
      case "neighborhood":
        return (
          <ContainerLineItem
            key={note.id}
            lineItemHeader={
              <ReportLink<PullNeighborhoodReportParams>
                noWrap
                inputValue={{
                  addressSearch: {
                    address: note.addressLine1,
                    addressLine2: note.addressLine2,
                    city: note.city,
                    state: note.state,
                    zipCode: note.zip,
                  },
                  searchMethod: "address",
                }}
                text={note.displayName}
                handleClick={pullNeighborhoodReport}
              />
            }
            lineItemSubHeader={note.reportDetails}
            lineItemSubHeaderLine2={
              <Text size="xs">
                <Text tag="b" size="xs">
                  {note.reportNotesCount} Note{note.reportNotesCount > 1 && "s"}
                </Text>{" "}
                - Modified on {parseDate(note.lastActivityDate, "MM/DD/YYYY")}
              </Text>
            }
            lineItemHeaderNoWrap
            lineItemSubheaderNoWrap
            lineItemDetails={
              <Layout
                type="flex"
                flexProps={{ gap: detailsSpacing, alignItems: "center" }}
              >
                <Icon size={32} name={IconList.IconNeighborhoodWatch} />
              </Layout>
            }
            noPadding={noPadding}
          />
        );
      case "vehicle":
        return (
          <ContainerLineItem
            key={note.id}
            lineItemHeader={
              <ReportLink
                noWrap
                inputValue={{ vin: note.vin }}
                text={note.displayName}
                handleClick={inputValue =>
                  pullVehicleReport(inputValue, "note")
                }
              />
            }
            lineItemSubHeader={note.reportDetails}
            lineItemSubHeaderLine2={
              <Text size="xs">
                <Text tag="b" size="xs">
                  {note.reportNotesCount} Note{note.reportNotesCount > 1 && "s"}
                </Text>{" "}
                - Modified on {parseDate(note.lastActivityDate, "MM/DD/YYYY")}
              </Text>
            }
            lineItemHeaderNoWrap
            lineItemSubheaderNoWrap
            lineItemDetails={
              <Layout
                type="flex"
                flexProps={{ gap: detailsSpacing, alignItems: "center" }}
              >
                <Icon size={32} name={IconList.IconVehicle} />
              </Layout>
            }
            noPadding={noPadding}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      {loading ? (
        <ContainerLineItem
          key={loadingKey}
          lineItemHeader={
            <LoadingPlaceholder width={"10rem"} height={"20px"} />
          }
          lineItemDetails={
            <Layout
              type="flex"
              flexProps={{ gap: detailsSpacing, alignItems: "center" }}
            >
              <LoadingPlaceholder width={"32px"} height={"32px"} />
              <div>
                <Layout
                  type="flex"
                  flexProps={{
                    gap: "xxs",
                    direction: "column",
                    alignItems: "flex-end",
                  }}
                >
                  <LoadingPlaceholder width={"55px"} height={"13px"} />
                  <LoadingPlaceholder width={"50px"} height={"13px"} />
                </Layout>
              </div>
            </Layout>
          }
          lineItemSubHeader={<LoadingPlaceholder width={"12rem"} />}
        />
      ) : (
        renderListItems(note)
      )}
    </>
  );
};

export default NoteLineItem;
